import React, { useEffect, useMemo, useState } from 'react';
import Icon from '../../../components/AppIcon';
import { ATTENDANCE_STATUSES, LEAVE_TYPES } from '../constants';
import { formatDateTimeLocalInput, toISOStringOrNull } from '../utils';

const defaultFormState = {
  user: '',
  date: '',
  status: 'Present',
  checkIn: '',
  checkOut: '',
  leaveType: '',
  remarks: '',
  isApproved: false
};

const AttendanceFormModal = ({
  open,
  mode = 'create',
  initialData,
  employeeOptions = [],
  employeesLoading = false,
  onClose,
  onSubmit
}) => {
  const initialState = useMemo(() => {
    if (!initialData) {
      return defaultFormState;
    }
    return {
      user: initialData.user?._id || initialData.user || '',
      date: initialData.date ? new Date(initialData.date).toISOString().split('T')[0] : '',
      status: initialData.status || 'Present',
      checkIn: formatDateTimeLocalInput(initialData.checkIn),
      checkOut: formatDateTimeLocalInput(initialData.checkOut),
      leaveType: initialData.leaveType || '',
      remarks: initialData.remarks || '',
      isApproved: Boolean(initialData.isApproved)
    };
  }, [initialData]);

  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(initialState);
    }
  }, [initialState, open]);

  if (!open) {
    return null;
  }

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!form.user || !form.date) {
      return;
    }

    const payload = {
      user: form.user,
      date: form.date,
      status: form.status,
      remarks: form.remarks?.trim() || undefined
    };

    if (form.checkIn) {
      payload.checkIn = toISOStringOrNull(form.checkIn);
    }
    if (form.checkOut) {
      payload.checkOut = toISOStringOrNull(form.checkOut);
    }
    if (form.leaveType) {
      payload.leaveType = form.leaveType;
    }
    if (mode === 'edit') {
      payload.isApproved = form.isApproved;
    }

    setSubmitting(true);
    try {
      const success = await onSubmit?.(payload);
      if (success) {
        onClose?.();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {mode === 'edit' ? 'Update Attendance' : 'Add Attendance'}
            </h3>
            <p className="text-sm text-gray-500">Capture presence, leave, and timings</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <Icon name="X" size={20} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Employee *</label>
              <select
                name="user"
                value={form.user}
                onChange={handleChange}
                disabled={mode === 'edit' || employeesLoading}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">
                  {employeesLoading ? 'Loading employees...' : 'Select employee'}
                </option>
                {employeeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Check In</label>
              <input
                type="datetime-local"
                name="checkIn"
                value={form.checkIn}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Check Out</label>
              <input
                type="datetime-local"
                name="checkOut"
                value={form.checkOut}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Status *</label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {ATTENDANCE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Leave Type</label>
              <select
                name="leaveType"
                value={form.leaveType}
                onChange={handleChange}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Not a leave</option>
                {LEAVE_TYPES.map((leave) => (
                  <option key={leave} value={leave}>
                    {leave}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Remarks</label>
            <textarea
              name="remarks"
              value={form.remarks}
              onChange={handleChange}
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Add any context or notes"
            />
          </div>

          {mode === 'edit' && (
            <label className="inline-flex items-center space-x-2 text-sm text-gray-700">
              <input
                type="checkbox"
                name="isApproved"
                checked={form.isApproved}
                onChange={handleChange}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span>Mark as approved</span>
            </label>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
            >
              {submitting ? 'Saving...' : mode === 'edit' ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AttendanceFormModal;
